// A PLACE HAS AN ADDRESS. Every directory location in the dashboard is its own
// URL (law_every_place_an_address, the stage): a space, a project, a folder, a
// document. The shell still moves without a server round trip — the address is
// written with history.pushState, which the App Router folds into its own
// state — but a refresh, a copied link or Back lands exactly there.
//
//   shell path            URL (base "/dashboard")
//   /projects             /dashboard/projects
//   /projects/p1          /dashboard/projects/p1
//   /projects/p1/vision   /dashboard/projects/p1/vision     (a document: the page's own path)
//   /you                  /dashboard/you
//   view chip             ?v=list                           (a way of looking, replaced, never pushed)
//
// Shell paths are built from `seg()` (lowercase a-z0-9 and dashes) and ids, so
// they are URL-safe as they stand; anything else is encoded segment by segment.

/** query keys that belong to one arrival and never ride along to the next place. */
const ARRIVAL_KEYS = ["panel", "open", "view", "v", "add", "day"];

export function urlFor(base: string, shellPath: string, opts?: { view?: string | null; search?: string }): string {
  const b = base.replace(/\/+$/, "");
  const segs = shellPath.split("/").filter(Boolean).map((s) => encodeURIComponent(decodeSafe(s)));
  const path = segs.length ? `${b}/${segs.join("/")}` : b || "/";
  const q = new URLSearchParams(opts?.search ?? "");
  for (const k of ARRIVAL_KEYS) q.delete(k);
  if (opts?.view) q.set("v", opts.view);
  const qs = q.toString();
  return qs ? `${path}?${qs}` : path;
}

/** the shell path a URL names under `base`, or null when it names none (the base itself). */
export function shellPathOf(base: string, pathname: string): string | null {
  const b = base.replace(/\/+$/, "");
  const p = pathname.replace(/\/+$/, "");
  if (p !== b && !p.startsWith(b + "/")) return null;
  const rest = p.slice(b.length).split("/").filter(Boolean).map(decodeSafe);
  if (!rest.length) return null;
  return "/" + rest.join("/");
}

/** a shell path from route params (`[[...path]]`). */
export function shellPathFromParams(parts: string[] | undefined): string | null {
  const rest = (parts ?? []).map(decodeSafe).filter(Boolean);
  return rest.length ? "/" + rest.join("/") : null;
}

/** the view chip a URL asks for (`?v=`). */
export function viewOf(search: string): string | null {
  try {
    const v = new URLSearchParams(search).get("v");
    return v && /^[a-z][a-z0-9-]{0,24}$/.test(v) ? v : null;
  } catch {
    return null;
  }
}

function decodeSafe(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}
